import {AfterViewInit, Component, inject, input, OnDestroy, ViewChild}     from '@angular/core';
import {MoodRecordLogIntf, PointWithData}                                  from '@djsharkk/logr-types';
import {ChartConfiguration, ChartOptions, LinearScaleOptions, TooltipItem} from 'chart.js';
import * as d3                                                             from 'd3';
import {DateTime}                                                          from 'luxon'; // Import D3 for scaleLinear
import {takeUntil}                                                         from 'rxjs';
import {UiNumberFormatterService}                                          from '../../../logr-ui/utils/ui-number-formatter.service';
import {ChartDoughnutComponent}                                            from '../../comps/chart-doughnut/chart-doughnut.component';
import {ChartLineComponent}                                                from '../../comps/chart-line/chart-line.component';
import {ChartCatsBase}                                                     from '../chart-cats.base';

@Component({
			   selector   : 'app-chart-mood-day',
			   imports    : [
				   ChartLineComponent
			   ],
			   templateUrl: './chart-mood-day.component.html',
			   styleUrl   : './chart-mood-day.component.css'
		   })
export class ChartMoodDayComponent extends ChartCatsBase<ChartDoughnutComponent, 'line'> implements OnDestroy, AfterViewInit {
	@ViewChild('ChartWrapper') chartComponent!: ChartDoughnutComponent;
	svcUiNumberFormatter = inject(UiNumberFormatterService)


	conf: ChartConfiguration<'line', PointWithData[]> = {
		data   : {
			datasets: [
				{
					data            : [],
					borderWidth     : 1,
					borderColor     : this.svcUiTheme.getTailwindColor('neutral', 900),
					pointRadius     : 3,
					pointStyle      : 'cross',
					pointBorderWidth: 1,
					pointHitRadius  : 5,
					datalabels      : {
						display  : 'auto',
						formatter: (value: PointWithData<MoodRecordLogIntf, 'mood'>) => value.d ? value.d.label.toUpperCase() : 'Error in Chart Data',
						align    : context => {
							const d = context.dataset.data[context.dataIndex] as PointWithData<MoodRecordLogIntf>
							return d.y > 0 ? 'top' : 'bottom'
						},
						anchor   : context => {
							const d = context.dataset.data[context.dataIndex] as PointWithData<MoodRecordLogIntf>
							return d.y > 0 ? 'end' : 'start'
						}
					}
				}
			],
			labels  : ['Today']
		},
		options: this.svcChartProps.optsDefault as ChartOptions<'line'>,
		plugins: [],
		type   : 'line'
	};

	date = input<Date>(new Date());

	constructor() {
		super();

		const tooltipConfig = {
			...this.conf.options?.plugins?.tooltip
		};

		if (this.conf.options?.plugins?.tooltip) {
			this.conf.options.plugins.tooltip = {
				...tooltipConfig,
				intersect: true,
				mode     : 'nearest',
				callbacks: {
					title     : () => '',
					label     : (tooltipItem: TooltipItem<'line'>) => {
						const dataPoint = tooltipItem.dataset.data[tooltipItem.dataIndex] as PointWithData;
						const d = dataPoint.d as MoodRecordLogIntf
						return d.label.toUpperCase()
					},
					afterLabel: (tooltipItem: TooltipItem<'line'>) => {
						const dataPoint = tooltipItem.dataset.data[tooltipItem.dataIndex] as PointWithData;
						const d = dataPoint.d as MoodRecordLogIntf
						return DateTime.fromMillis(d.timestamp.toMillis()).toFormat('HH:mm')
					}
				}
			}
		}

		const defaultScales = {
			...this.conf.options?.scales
		};

		const defaultX: LinearScaleOptions = {
			...defaultScales?.['x'] as LinearScaleOptions
		};

		const defaultY: LinearScaleOptions = {
			...defaultScales?.['y'] as LinearScaleOptions
		};


		if (this.conf.options?.scales) {
			this.conf.options.scales = {
				...defaultScales,
				x: {
					...defaultX,
					type  : 'linear',
					min   : -7,
					max   : 7,
					offset: false,
					ticks : {
						...defaultX?.ticks,
						display : false,
						stepSize: 1
					},
					grid  : {
						...defaultX?.grid,
						display: true
					}
				} as LinearScaleOptions,
				y: {
					...defaultY,
					min   : -7,
					max   : 7,
					offset: false,
					ticks : {
						...defaultY?.ticks,
						display : false,
						stepSize: 1
					},
					grid  : {
						...defaultY?.grid,
						display: true
					}
				} as LinearScaleOptions
			}
		}


	}


	public ngAfterViewInit() {
		this.svcDataFactory.svcDataMood.getRecordsLogDates([this.date()])
			.pipe(takeUntil(this.dataTrash))
			.subscribe((records) => {
				const timestamps = records.map(record => record.timestamp.toMillis());

				const minTimestamp = Math.min(...timestamps);
				const maxTimestamp = Math.max(...timestamps);

				/*	const colorScale = d3.scaleSequential(d3.interpolateYlGnBu)
				 .domain([
				 minTimestamp,
				 maxTimestamp
				 ]);*/

				const colorScale = d3.scaleLinear<string>() // Use d3's scaleLinear
									 .domain([
												 minTimestamp,
												 maxTimestamp
											 ])
									 .range([
												this.svcUiTheme.getTailwindColor('blue', 600),
												this.svcUiTheme.getTailwindColor('lime', 600)
											]);


				// Update the dataset with point colors and gradient for the line
				this.chartComponent?.updateDataset(0, {
					data            : records.map(record => ({
						x: record.x,
						y: record.y,
						d: record
					})),
					pointBorderColor: records.map(record => (colorScale(record.timestamp.toMillis()))),
					datalabels      : {
						...this.conf.data.datasets[0].datalabels,
						color: records.map(record => (colorScale(record.timestamp.toMillis())))
					}
				});
			});
	}
}